import React, { FC, ReactNode, useMemo } from 'react';
import { Menu } from 'antd';
import { CountBadge } from 'common/components';
import { ESearchParams } from 'common/const';
import { getFilterOptions } from 'common/helpers';
import { IOption } from 'common/models';
import { UserHooks, useSearchParamsHook } from 'common/hooks';
import { useAppSelector } from 'app/store/store.hooks';
import {
  AvailableInventoryListTab,
  WriteOffInventoryActionListForUserTab,
  WriteOffInventoryListTab,
} from 'entities/Inventory/components';
import { EInventoryListTab } from 'entities/Inventory/Inventory.enum';
import { selectInventoryListSubdivisions, selectInventoryListUsers } from 'entities/Inventory/Inventory.slice';
import { selectUserInventoryStats } from 'entities/User/User.slice';

interface IComponentProps {
  header: ReactNode;
}

export const InventoryListTab: FC<IComponentProps> = ({ header }) => {
  // inventory
  const inventoryListSubdivisions = useAppSelector(selectInventoryListSubdivisions);
  const inventoryListUsers = useAppSelector(selectInventoryListUsers);
  // user
  const userInventoryStats = useAppSelector(selectUserInventoryStats);

  const { getSearchParam, setSearchParam } = useSearchParamsHook();
  const { isAccountAdmin, isHeadOfDepartment } = UserHooks.useCurrentUserRole();

  const { inventoryAvailableCount, inventoryWrittenOffCount, writeOffsCount } = userInventoryStats;
  const tab = getSearchParam(ESearchParams.Tab);
  const subdivisionOptions = getFilterOptions.subdivision(inventoryListSubdivisions);
  const userOptions = getFilterOptions.userWithNull(inventoryListUsers);

  const menu = useMemo(() => {
    return (
      <Menu
        items={[
          {
            key: EInventoryListTab.Available,
            label: (
              <>
                Инструменты в наличии
                <CountBadge count={inventoryAvailableCount} />
              </>
            ),
          },
          ...(!(isAccountAdmin || isHeadOfDepartment)
            ? [
                {
                  key: EInventoryListTab.WriteOffActionList,
                  label: (
                    <>
                      Запросы о списании
                      <CountBadge count={writeOffsCount} />
                    </>
                  ),
                },
              ]
            : []),
          {
            key: EInventoryListTab.WriteOff,
            label: (
              <>
                Списанные инструменты
                <CountBadge count={inventoryWrittenOffCount} />
              </>
            ),
          },
        ]}
        selectedKeys={[tab === null ? EInventoryListTab.Available : tab]}
        onClick={({ key }) => setSearchParam(ESearchParams.Tab, key)}
        mode="horizontal"
      />
    );
  }, [tab, userInventoryStats]);

  const renderContent = () => {
    switch (tab) {
      case EInventoryListTab.WriteOffActionList: {
        return <WriteOffInventoryActionListForUserTab header={header} menu={menu} />;
      }
      case EInventoryListTab.WriteOff: {
        return (
          <WriteOffInventoryListTab
            header={header}
            menu={menu}
            subdivisionOptions={subdivisionOptions}
            userOptions={userOptions as IOption[]}
          />
        );
      }
      default: {
        return (
          <AvailableInventoryListTab
            header={header}
            menu={menu}
            subdivisionOptions={subdivisionOptions}
            userOptions={userOptions as IOption[]}
          />
        );
      }
    }
  };

  return renderContent();
};
